// Onboarding: what its address can open. No React in here.
//
// The worklist shows one plan tab (Active or Completed), narrowed by an onboarding score
// group or, on Active, by the Filters window's Buddy filter set to "Waiting for a buddy"
// alone. Either is one value in the address (?list=<slug>), so the home screen and a
// colleague's link land on the same rows the person who sent it was looking at. The
// window's other filters are not in the address.
import type { DashboardListInfo } from './types';

export type OnboardingPlan = 'active' | 'completed';
export type OnboardingScoreGroup = 'ready' | 'extend' | 'not_enough' | 'in_training' | 'decided';

/** The plan tabs, by the names the dashboard prints on them. */
export const ONBOARDING_PLANS: ReadonlyArray<{ key: OnboardingPlan; label: string; line: string }> = [
  { key: 'active', label: 'Active', line: 'Joiners whose plan is still running.' },
  { key: 'completed', label: 'Completed', line: 'Plan ended recently. They can still be extended.' },
];

/**
 * The onboarding score groups, in the order their chips are drawn. The label and the
 * hover text are the chip's own (the dashboard reads them from here), so a list is
 * never named one way on the chip and another way in page search.
 */
export const ONBOARDING_SCORE_GROUPS: ReadonlyArray<{ key: OnboardingScoreGroup; slug: string; label: string; title: string }> = [
  {
    key: 'ready', slug: 'ready', label: 'Ready to deploy',
    title: 'Shadow training is complete and the score is at or above the pass mark. No decision recorded yet.',
  },
  {
    key: 'extend', slug: 'extend', label: 'Extension advised',
    title: 'Shadow training is complete and the score is below the pass mark. No decision recorded yet.',
  },
  {
    key: 'not_enough', slug: 'not-enough', label: 'Not enough ratings',
    title: 'Shadow training is complete but too few days were rated to rely on the score. No decision recorded yet.',
  },
  {
    key: 'in_training', slug: 'in-training', label: 'In shadow training',
    title: 'Shadow training has not finished yet, so there is no advice.',
  },
  {
    key: 'decided', slug: 'decided', label: 'Decided',
    title: 'A coordinator has recorded a deploy or extend decision.',
  },
];

/** What the worklist is showing. */
export interface OnboardingView {
  plan: OnboardingPlan;
  /** The score group the table is narrowed to, or null. */
  score: OnboardingScoreGroup | null;
  /** Buddy filter on "Waiting for a buddy" alone. Active only, and never with a score group:
   *  beside one, the dashboard writes the score group's slug and keeps Buddy itself. */
  noBuddy: boolean;
}

/** The slug the home screen links to: Active, narrowed to joiners with no buddy. */
export const NO_BUDDY_SLUG = 'no-buddy';

/**
 * That list's name and its one line. The Buddy filter's choice reads the name from here
 * too, so the filter and page search name the list alike.
 */
export const NO_BUDDY_LIST = {
  label: 'Waiting for a buddy',
  title: 'Active joiners with no buddy and no store yet. Until one is assigned they cannot sign in to the mobile app.',
} as const;

/** Plain Active: what the dashboard shows when the address names no list. */
export const PLAIN_ACTIVE: OnboardingView = { plan: 'active', score: null, noBuddy: false };

/** The slug for a view. Plain Active has one too ('active'), though the dashboard opens on it without. */
export function onboardingSlug(view: OnboardingView): string {
  if (view.noBuddy) return NO_BUDDY_SLUG;
  const group = view.score ? ONBOARDING_SCORE_GROUPS.find((g) => g.key === view.score) : undefined;
  return group ? `${view.plan}-${group.slug}` : view.plan;
}

/** Whether a view is plain Active, which the dashboard shows at its bare address. */
export const isDefaultOnboardingView = (view: OnboardingView): boolean =>
  view.plan === PLAIN_ACTIVE.plan && view.score === null && !view.noBuddy;

/** The view a slug names. No slug, or one this dashboard does not know, is plain Active. */
export function onboardingViewFromSlug(slug: string | null | undefined): OnboardingView {
  const wanted = (slug ?? '').trim().toLowerCase();
  if (wanted === NO_BUDDY_SLUG) return { plan: 'active', score: null, noBuddy: true };
  for (const { key: plan } of ONBOARDING_PLANS) {
    if (wanted === plan) return { plan, score: null, noBuddy: false };
    const group = ONBOARDING_SCORE_GROUPS.find((g) => wanted === `${plan}-${g.slug}`);
    if (group) return { plan, score: group.key, noBuddy: false };
  }
  return PLAIN_ACTIVE;
}

/** Every list on Onboarding that can be opened by address. */
export const LISTS: DashboardListInfo[] = [
  ...ONBOARDING_PLANS.map((p) => ({ slug: p.key, name: p.label, line: p.line })),
  {
    slug: NO_BUDDY_SLUG,
    name: NO_BUDDY_LIST.label,
    line: NO_BUDDY_LIST.title,
    keywords: 'no buddy unassigned not assigned',
  },
  ...ONBOARDING_PLANS.flatMap((p) => ONBOARDING_SCORE_GROUPS.map((g) => ({
    slug: onboardingSlug({ plan: p.key, score: g.key, noBuddy: false }),
    name: `${p.label} › ${g.label}`,
    line: g.title,
    keywords: 'onboarding score',
  }))),
];
